import { Box, Checkbox, IconButton, InputAdornment, styled } from '@mui/material';
import { Search } from '@styled-icons/bootstrap';
import { Close } from '@styled-icons/ionicons-sharp';
import { Loader } from 'components/Forms';
import { SearchInput } from 'components/Forms/SearchToolbar';
import { EmptySearchResults, ErrorState, InfiniteScrollWrapper } from 'components/Structure';
import React, { FC } from 'react';

import useSites from 'hooks/useSites';
import { ISite, ESiteType, SiteViewOption } from 'types/site.types';
import SitesTable from 'components/Sites/SitesTable/SitesTable';
import { SitesTableColumnKeys } from 'components/Sites/SitesTable/sitesHeaderColumns';
import { useDialog } from 'components/hooks';

interface ISiteSelectionProps {
  selectedSiteIds: string[];
  selectedClusterIds: string[];
  setSelectedSiteIds: (ids: string[]) => void;
  setSelectedClusterIds: (ids: string[]) => void;
}

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: 0,
  margin: 0,
}));

const SiteSelection: FC<ISiteSelectionProps> = ({
  selectedClusterIds,
  selectedSiteIds,
  setSelectedClusterIds,
  setSelectedSiteIds,
}) => {
  const {
    sites,
    hasNextPage,
    loading,
    error,
    searchTerm,
    handleSearchTermChange,
    handlePageEndReached,
  } = useSites({
    skipUseQueryParams: true,
    excludeEmptyClusters: true,
    clusterSitesType: ESiteType.FARM,
    defaultViewOption: SiteViewOption.CLUSTER,
    defaultSiteTypes: [ESiteType.CLUSTER, ESiteType.FARM],
  });
  const { openDialog } = useDialog();

  const handleSelect = (site: ISite) => {
    if (site.siteType === ESiteType.CLUSTER) {
      const isSelected = selectedClusterIds.includes(site.id);
      if (!isSelected) {
        setSelectedClusterIds([...selectedClusterIds, site.id]);
      } else {
        setSelectedClusterIds(selectedClusterIds.filter(clusterId => clusterId !== site.id));
      }
    } else {
      const isSelected = selectedSiteIds.includes(site.id);
      if (!isSelected) {
        setSelectedSiteIds([...selectedSiteIds, site.id]);
      } else {
        setSelectedSiteIds(selectedSiteIds.filter(siteId => siteId !== site.id));
      }
    }
  };

  const handleToggleAll = () => {
    const allIds = sites.map(({ id }) => id);
    const selectedIds = allIds.filter(
      id => selectedSiteIds.includes(id) || selectedClusterIds.includes(id)
    );

    if (selectedIds.length === allIds.length) {
      setSelectedSiteIds([]);
      setSelectedClusterIds([]);
    } else {
      setSelectedSiteIds(
        sites.filter(({ siteType }) => siteType !== ESiteType.CLUSTER).map(({ id }) => id)
      );
      setSelectedClusterIds(
        sites.filter(({ siteType }) => siteType === ESiteType.CLUSTER).map(({ id }) => id)
      );
    }
  };

  const handleViewSitePlots = (site: ISite) => {
    openDialog({
      type: 'GEO_DATA_PLOT_OVERVIEW',
      props: {
        site,
      },
    });
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Checkbox
          checked={selectedSiteIds.length + selectedClusterIds.length === sites.length}
          onClick={handleToggleAll}
        />
        <SearchInput
          setDebouncedState={handleSearchTermChange}
          className="search-input"
          data-cy="dataset-search-input"
          delay={500}
          placeholder="Search"
          initialValue={searchTerm}
          inputProps={{
            endAdornment: (
              <InputAdornment position="start">
                {searchTerm === '' ? (
                  <Search size={12} />
                ) : (
                  <CloseButton onClick={() => handleSearchTermChange('')}>
                    <Close size={16} />
                  </CloseButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {loading && (
        <Box position="relative" height="100%" p={5}>
          <Loader />
        </Box>
      )}

      {error && (
        <Box position="relative" height="100%" p={10}>
          <ErrorState error={error} />
        </Box>
      )}

      {!loading && sites.length === 0 && (
        <Box position="relative" p={15}>
          <EmptySearchResults skeletonComponent={searchTerm || ''} />
        </Box>
      )}
      {!loading && sites.length > 0 && (
        <Box zIndex={0} position="relative" width="100%">
          <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
            <SitesTable
              sites={sites}
              hideHeader
              clusterSitesDisabled
              siteClusterBaseLink="/sites"
              selectedSiteIds={[...selectedSiteIds, ...selectedClusterIds]}
              onSiteSelect={handleSelect}
              onSiteClick={handleSelect}
              onViewSitePlots={handleViewSitePlots}
              columns={[
                SitesTableColumnKeys.SELECTION,
                SitesTableColumnKeys.TITLE,
                SitesTableColumnKeys.MODIFIED_TIMESTAMP,
                SitesTableColumnKeys.ACTIONS,
              ]}
            />
          </InfiniteScrollWrapper>
        </Box>
      )}
    </Box>
  );
};

export default SiteSelection;
